import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import usePageMeta from "../lib/usePageMeta";
import { Button } from "../components/ui/button";
import { Linkedin, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const TEAM = [
  {
    id: "jr-faour",
    name: "Jr Faour",
    role: "CEO / Founder",
    photo:
      "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/qzd56q7k_07222282-6138-4df5-acba-7df36c1f8fd0.png",
    bio:
      "Jr founded American Asset Recovery Agency after seeing too many families lose track of surplus funds they were legally owed. With years of experience navigating county records, court filings, and state-specific claim procedures, he leads the agency's day-to-day mission: put money back in the hands of the homeowners it belongs to.",
    quote:
      "Every family deserves a straightforward path to what's rightfully theirs — no upfront fees, no gimmicks.",
    email: "recover@americanassetrecoveryagency.com",
    linkedin: "",
    photoFilter: "",
  },
  {
    id: "erika-escobar",
    name: "Erika Escobar",
    role: "Vice President",
    photo:
      "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/baegeo4b_Erika%20Escobar2.jpeg",
    bio:
      "Erika brings a strong financial background to AARA — before joining the team she served as the CFO of a fashion company, handling bookkeeping, budgeting, financial reporting, and full-cycle back-office operations. She brings that same discipline to her role as Vice President, overseeing client onboarding, case management, and the operational engine that keeps every claim moving, and translating complex county paperwork into clear next steps for our families.",
    quote:
      "The paperwork can feel overwhelming — my job is to make sure our clients never feel lost in it.",
    email: "recover@americanassetrecoveryagency.com",
    linkedin: "",
    photoFilter: "",
  },
  {
    id: "gilberto-toro",
    name: "Gilberto Toro",
    role: "Recovery Specialist",
    photo:
      "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/h4mpsw8s_IMG-20260702-WA0045%281%29.jpg",
    bio:
      "Gilberto works directly with our clients to research county surplus records, prepare claim documentation, and shepherd cases through court filings. His attention to detail and calm follow-through make him the person our families count on to keep momentum when a claim needs one more signature, one more affidavit, or one more phone call.",
    quote:
      "Every case has a real person behind it. My job is to keep things moving so you don't have to chase the courthouse.",
    email: "recover@americanassetrecoveryagency.com",
    linkedin: "",
    // Lighten his darker studio gradient to visually match the softer backgrounds of the other two photos
    photoFilter: "brightness(1.12) contrast(0.92) saturate(0.95)",
    // Shift crop upward to hide the small watermark near the bottom edge of the source image
    photoPosition: "50% 15%",
  },
  {
    id: "monica-nicolas",
    name: "Monica Nicolas",
    role: "Master Recovery Specialist",
    photo:
      "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/lnfvexog_Monica.png",
    bio:
      "Monica is our most seasoned recovery specialist and the person we escalate the toughest cases to — probate complications, disputed heirs, multi-lienholder claims, and out-of-state filings. She combines deep knowledge of state-specific surplus statutes with a client-first temperament that keeps families informed and confident from petition through funding.",
    quote:
      "The complicated cases are usually the ones where families need us most. I love turning a 'no way' into a check in hand.",
    email: "recover@americanassetrecoveryagency.com",
    linkedin: "",
    photoFilter: "",
    // Photo is landscape framing — anchor near the top so her face is prominent in the 4:5 crop
    photoPosition: "50% 20%",
  },
  {
    id: "alfert-santiago",
    name: "Dr. Alfert Santiago",
    role: "Real Estate Investor",
    photo:
      "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/yb2n1cy0_Dr.%20Alfert%202.jpeg",
    bio:
      "Dr. Santiago is a seasoned real estate investor whose market experience informs how we evaluate every claim. From distressed-property fundamentals to post-foreclosure sale patterns across counties, his insight helps our team spot which cases will yield the strongest surplus recoveries — and gives our clients confidence that the numbers behind their claim are grounded in real market knowledge.",
    quote:
      "Behind every foreclosure file is a family and a property with a story. Understanding the market is how we make sure that story doesn't end at the courthouse.",
    email: "recover@americanassetrecoveryagency.com",
    linkedin: "",
    // Deepen and enrich the suit while keeping face natural (subtle darken + higher contrast + slight saturation lift)
    photoFilter: "brightness(0.9) contrast(1.15) saturate(1.15)",
  },
];

const VALUES = [
  {
    title: "No upfront fees, ever",
    body:
      "We only get paid when you get paid. If we don't recover funds, you owe us nothing — that promise is non-negotiable.",
  },
  {
    title: "Radical transparency",
    body:
      "Every claim is documented, every step explained. You'll know exactly where your case stands from filing to funding.",
  },
  {
    title: "Homeowner-first",
    body:
      "We built this company because the surplus funds system is stacked against the people it's supposed to protect. We're here to level it.",
  },
];

export default function Team() {
  usePageMeta({
    title: "Meet the Team · American Asset Recovery Agency",
    description:
      "Meet the team behind American Asset Recovery Agency — real people helping families across all 48 contiguous states recover surplus foreclosure funds they're rightfully owed.",
    canonical: "https://americanassetrecoveryagency.com/team",
  });

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="team-page">
      <Header />

      <main className="pt-32 pb-24">
        {/* Intro */}
        <section className="max-w-6xl mx-auto px-6">
          <Reveal>
            <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">
              Meet the Team
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] leading-tight max-w-4xl">
              Real people. Real work. Recovering what&apos;s rightfully yours.
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-2xl leading-relaxed">
              Behind every claim is a small, dedicated team that treats each case
              like it belongs to their own family. We built American Asset Recovery
              Agency because homeowners deserve a straight answer, a straight path,
              and a partner who has done this before.
            </p>
          </Reveal>
        </section>

        {/* Team grid */}
        <section className="max-w-7xl mx-auto px-6 mt-16 sm:mt-20">
          <div className="flex flex-wrap justify-center gap-6 lg:gap-8">
            {TEAM.map((member, i) => (
              <Reveal key={member.id} delay={i * 0.12} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-22px)]">
                <article
                  className="group bg-white rounded-3xl border border-[#E2DDD3] shadow-sm hover:shadow-lg transition-all duration-500 overflow-hidden h-full flex flex-col"
                  data-testid={`team-card-${member.id}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEAE0]">
                    <img
                      src={member.photo}
                      alt={`${member.name}, ${member.role}`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      style={{
                        ...(member.photoFilter ? { filter: member.photoFilter } : {}),
                        ...(member.photoPosition ? { objectPosition: member.photoPosition } : {}),
                      }}
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    <div className="absolute bottom-5 left-6 right-6 text-[#F7F5F0]">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#FF008F] font-bold">
                        {member.role}
                      </p>
                      <h2 className="font-serif-display text-3xl leading-tight mt-1">
                        {member.name}
                      </h2>
                    </div>
                  </div>

                  <div className="p-7 sm:p-8 flex flex-col flex-1">
                    <p className="text-[#475569] leading-relaxed flex-1">{member.bio}</p>

                    <blockquote className="mt-6 border-l-2 border-[#FF008F] pl-4 italic text-[#0F172A] sm:min-h-[7.5rem]">
                      &ldquo;{member.quote}&rdquo;
                    </blockquote>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] hover:text-[#FF008F] transition-colors"
                          data-testid={`team-email-${member.id}`}
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </a>
                      )}
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] hover:text-[#FF008F] transition-colors"
                          data-testid={`team-linkedin-${member.id}`}
                        >
                          <Linkedin className="w-4 h-4" />
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="max-w-6xl mx-auto px-6 mt-24 sm:mt-28">
          <Reveal>
            <div className="max-w-3xl">
              <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">
                What we stand for
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
                Three commitments that shape every case.
              </h2>
            </div>
          </Reveal>

          <div className="mt-10 sm:mt-12 grid md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div
                  className="h-full bg-white rounded-2xl border border-[#E2DDD3] p-6 sm:p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`team-value-${i}`}
                >
                  <p className="font-serif-display text-2xl text-[#0F172A] leading-tight">
                    {v.title}
                  </p>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-6 mt-24 sm:mt-28">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] px-8 py-14 sm:px-14 sm:py-20 text-center">
              <div className="absolute inset-0 opacity-20 bg-gradient-to-br from-[#FF008F]/40 via-transparent to-[#1E3A8A]/40" />
              <div className="relative">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF008F]">
                  Ready to talk?
                </p>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#F7F5F0] mt-3 leading-tight max-w-3xl mx-auto">
                  Let&apos;s find out if surplus funds are waiting for you.
                </h2>
                <p className="mt-5 text-[#F7F5F0]/75 max-w-xl mx-auto">
                  Free claim check. No upfront fees. You only pay if we recover.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/#claim">
                    <Button
                      className="bg-[#FF008F] hover:bg-[#D60079] text-white rounded-full px-7 h-12 text-base font-bold transition-all duration-300 hover:-translate-y-0.5 shadow-xl shadow-[#FF008F]/30"
                      data-testid="team-cta-claim"
                    >
                      Check My Free Claim <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href="tel:+18335359505" data-testid="team-cta-call">
                    <Button
                      variant="outline"
                      className="border-[#F7F5F0]/30 bg-transparent text-[#F7F5F0] hover:bg-[#F7F5F0]/10 hover:text-[#F7F5F0] rounded-full px-7 h-12 text-base font-semibold"
                    >
                      Call 1-833-535-9505
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
}
